import { motion, useReducedMotion, useInView, useMotionValue, animate } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  fadeUp, fadeUpSmall, fadeIn, heroFadeUp, staggerParent, revealViewport,
} from "@/lib/motion";

/* ================================================================== *
 * <Reveal> — the primary building block. Fades + slides its child     *
 * upward on scroll. `variant` allows the caller to switch between     *
 * the "hero" tempo and the standard tempo.                            *
 * ================================================================== */
export function Reveal({
  as: Tag = "div",
  variant = "fadeUp",
  delay = 0,
  className = "",
  children,
  ...rest
}) {
  const reduce = useReducedMotion();
  const chosen = variant === "hero" ? heroFadeUp
             : variant === "small" ? fadeUpSmall
             : variant === "fadeIn" ? fadeIn
             : fadeUp;

  const withDelay = delay
    ? { ...chosen, show: { ...chosen.show, transition: { ...chosen.show.transition, delay } } }
    : chosen;

  if (reduce) {
    return <Tag className={className} {...rest}>{children}</Tag>;
  }

  const MotionTag = motion[Tag] || motion.div;
  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={revealViewport}
      variants={withDelay}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/* ================================================================== *
 * <StaggerGroup> — parent that reveals children one-by-one.           *
 * ================================================================== */
export function StaggerGroup({
  as: Tag = "div",
  stagger = 0.08,
  delay = 0,
  className = "",
  children,
  ...rest
}) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <Tag className={className} {...rest}>{children}</Tag>;
  }

  const MotionTag = motion[Tag] || motion.div;
  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={revealViewport}
      variants={staggerParent(stagger, delay)}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/* Child of <StaggerGroup> — inherits the parent variant. */
export function StaggerItem({
  as: Tag = "div",
  variant = "fadeUp",
  className = "",
  children,
  ...rest
}) {
  const reduce = useReducedMotion();
  const chosen = variant === "small" ? fadeUpSmall : fadeUp;
  if (reduce) return <Tag className={className} {...rest}>{children}</Tag>;
  const MotionTag = motion[Tag] || motion.div;
  return (
    <MotionTag className={className} variants={chosen} {...rest}>
      {children}
    </MotionTag>
  );
}

/* ================================================================== *
 * <CountUp> — numeric counter that fires when it enters the viewport. *
 *                                                                     *
 * Formats accept:  suffix, prefix, decimals, and a divisor for        *
 * "Mt", "$M", etc.                                                    *
 * ================================================================== */
export function CountUp({
  to,
  from = 0,
  duration = 1.6,
  decimals = 0,
  prefix = "",
  suffix = "",
  className = "",
  testid,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const reduce = useReducedMotion();
  const mv = useMotionValue(from);
  const [display, setDisplay] = useState(from);

  useEffect(() => {
    if (!inView) return;
    if (reduce) { setDisplay(to); return; }
    const controls = animate(mv, to, {
      duration,
      ease: [0.22, 0.61, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return controls.stop;
  }, [inView, to, duration, reduce, mv]);

  const shown = decimals ? display.toFixed(decimals) : Math.round(display).toString();
  return (
    <span ref={ref} className={className} data-testid={testid}>
      {prefix}{shown}{suffix}
    </span>
  );
}
